import { Component, Prop, Vue } from 'vue-property-decorator'
import ConfigurationRelationships from '@/modules/configurationModule/shared/components/configuration-relationships/configuration-relationships.vue'
import { CommonFilterModel, CommonParams } from '@/api/resources/common/shared/models/common.models'
import { ConfigurationProtocolResponse } from '@/modules/configurationModule/detail/local-assays/protocols/services/configuration-protocol/configuration-protocol.dto'
import {
    ConfigurationLocalAssaysProtocolsModalAssaysFormState,
    initialConfigurationLocalAssaysProtocolsModalAssaysFormState
} from '@/modules/configurationModule/detail/local-assays/protocols/components/modal-assays-form/configuration-local-assays-protocols-modal-assays-form.model'
import {
    ConfigurationProtocolAssayCreateRequest,
    ConfigurationProtocolAssayResponse
} from '@/modules/configurationModule/detail/local-assays/protocols/services/configuration-protocol-assay/configuration-protocol-assay.dto'
import { configurationProtocolAssayService } from '@/modules/configurationModule/detail/local-assays/protocols/services/configuration-protocol-assay/configuration-protocol-assay.service'
import { EntityIdentifiersRequest, EntityIdentifierWithErrorResponse } from '@/shared/models/http/http.model'
import { PAGINATOR_BIG_PAGE_SIZE } from '@/shared/services/services-constants'
import { ConfigurationAssayLaboratoryResponse } from '@/modules/configurationModule/detail/local-assays/assays-laboratory/services/configuration-assay-laboratory/configuration-assay-laboratory.dto'
import { configurationAssayLaboratoryService } from '@/modules/configurationModule/detail/local-assays/assays-laboratory/services/configuration-assay-laboratory/configuration-assay-laboratory.service'
import { GridUtils } from '@/shared/util/grid-utils'

@Component({
    components: {
        ConfigurationRelationships
    }
})
export default class ConfigurationLocalAssaysProtocolsModalAssaysForm extends Vue {
    @Prop(String) readonly actionTitle!: string
    @Prop() readonly protocol!: ConfigurationProtocolResponse

    state: ConfigurationLocalAssaysProtocolsModalAssaysFormState
    constructor() {
        super()
        this.state = initialConfigurationLocalAssaysProtocolsModalAssaysFormState()
    }

    private async _loadProtocolAssays(): Promise<void> {
        this.state.protocolAssays = await this._getProtocolAssays()
    }

    async mounted() {
        await this._loadProtocolAssays()
    }

    getOptionName(assayLaboratory: ConfigurationAssayLaboratoryResponse): string {
        return assayLaboratory.assay.name
    }
    getRelationshipName(protocolAssay: ConfigurationProtocolAssayResponse): string {
        return protocolAssay.assay.name
    }

    /// SERVICES CALLS ///

    async getAssays(searchText: string = ''): Promise<ConfigurationAssayLaboratoryResponse[]> {
        const filterModel = {
            'assay.name': GridUtils.getTextFilterModel('contains', searchText)
        }
        const params: CommonParams = {
            size: PAGINATOR_BIG_PAGE_SIZE
        }
        const response =
            await this.$_toCallSecure(
                configurationAssayLaboratoryService.paginator(filterModel, params, this.state.laboratoryId))
        return response?.content ?? []
    }

    private async _createProtocolAssay(createRequest: ConfigurationProtocolAssayCreateRequest): Promise<ConfigurationProtocolAssayResponse | null> {
        const response = await this.$_toCallUnique(configurationProtocolAssayService.create(this.state.laboratoryId, createRequest))
        if (response) {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.created'))
            return response
        }
        return null
    }

    private async _getProtocolAssays(): Promise<ConfigurationProtocolAssayResponse[]> {
        const filterModel: CommonFilterModel = {
            'protocol.prtId': GridUtils.getNumberFilterModel('equals', this.protocol.id)
        }
        const params: CommonParams = {
            size: PAGINATOR_BIG_PAGE_SIZE
        }
        const response = await this.$_toCallUnique(configurationProtocolAssayService.paginator(filterModel, params, this.state.laboratoryId))
        if (response) {
            return response.content
        }
        return []
    }

    private async _deleteProtocolAssay(requestDelete: EntityIdentifiersRequest): Promise<EntityIdentifierWithErrorResponse[] | null> {
        const ids = requestDelete?.entityIdentifiers
        if (ids?.length) {
            const response = await this.$_toCallUnique(configurationProtocolAssayService.delete(this.state.laboratoryId, requestDelete))
            if (response) {
                const elements = ids.length - response.length
                if (elements > 0) {
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted'))
                }
                return response
            }
        }
        return null
    }

    /// FORM OPERATIONS ///

    async createOperation(assayLaboratory: ConfigurationAssayLaboratoryResponse): Promise<void> {
        if (assayLaboratory) {
            const createRequest: ConfigurationProtocolAssayCreateRequest = {
                protocolId: this.protocol.id,
                assayId: assayLaboratory.assay.id
            }
            if (await this._createProtocolAssay(createRequest)) {
                await this._loadProtocolAssays()
            }
        }
    }

    async deleteOperation(protocolAssay: ConfigurationProtocolAssayResponse): Promise<void> {
        if (protocolAssay) {
            const requestDelete: EntityIdentifiersRequest = {
                entityIdentifiers: [protocolAssay.id]
            }
            if (await this._deleteProtocolAssay(requestDelete)) {
                await this._loadProtocolAssays()
            }
        }
    }
}
